import React, { useState, useEffect, useMemo } from 'react';
import api, { formatApiErrorDetail, getPublicUploadsOrigin } from '../services/api';
import NotificationModal from '../components/NotificationModal';

const Evaluacion = () => {
  const [convocatorias, setConvocatorias] = useState([]);
  const [convocatoriasOriginales, setConvocatoriasOriginales] = useState([]);
  const [selectedConvocatoria, setSelectedConvocatoria] = useState(null);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [cotizacionesOriginales, setCotizacionesOriginales] = useState([]);
  const [evaluaciones, setEvaluaciones] = useState({}); // Mapa de id_cotizacion -> evaluacion
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingCotizacion, setEditingCotizacion] = useState(null);
  const [evaluacionData, setEvaluacionData] = useState({});
  const [fichaCalificacionFile, setFichaCalificacionFile] = useState(null);
  
  // Filtros y búsqueda
  const [searchProveedor, setSearchProveedor] = useState('');
  const [filterEvaluadas, setFilterEvaluadas] = useState('TODAS');
  
  // Búsqueda de selección de convocatoria
  const [searchSelectConvocatoria, setSearchSelectConvocatoria] = useState('');
  const [showConvocatoriaDropdown, setShowConvocatoriaDropdown] = useState(false);
  
  // Estados para notificaciones
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null
  });

  // Cargar convocatorias al montar
  useEffect(() => {
    loadConvocatorias();
  }, []);

  // Cargar cotizaciones cuando se selecciona una convocatoria
  useEffect(() => {
    if (selectedConvocatoria) {
      loadCotizacionesByConvocatoria(selectedConvocatoria);
      // Actualizar el texto del input cuando se selecciona una convocatoria
      const conv = convocatoriasOriginales.find(c => c.nro_convocatoria === selectedConvocatoria);
      if (conv) {
        setSearchSelectConvocatoria(`${conv.COD_CONVOCATORIA} - ${conv.concepto || 'Sin concepto'}`);
      }
    } else {
      setCotizaciones([]);
    }
  }, [selectedConvocatoria, convocatoriasOriginales]);

  const loadConvocatorias = async () => {
    try {
      const response = await api.get('/api/v1/convocatorias/');
      const data = response.data || [];
      setConvocatoriasOriginales(data);
      setConvocatorias(data);
    } catch (err) {
      console.error('Error loading convocatorias:', err);
      setError('Error al cargar las convocatorias');
    }
  };

  const loadCotizacionesByConvocatoria = async (nroConvocatoria) => {
    try {
      setLoading(true);
      setError(null);
      
      // Cargar cotizaciones
      const cotizacionesResponse = await api.get(`/api/v1/cotizaciones/convocatoria/${nroConvocatoria}`);
      const cotizacionesData = cotizacionesResponse.data || [];
      setCotizacionesOriginales(cotizacionesData);
      setCotizaciones(cotizacionesData);
      
      // Cargar evaluaciones
      try {
        const evaluacionesResponse = await api.get(`/api/v1/evaluaciones/convocatoria/${nroConvocatoria}`);
        const evaluacionesMap = {};
        (evaluacionesResponse.data || []).forEach(evaluacion => {
          evaluacionesMap[evaluacion.id_cotizacion] = evaluacion;
        });
        setEvaluaciones(evaluacionesMap);
      } catch (evalErr) {
        // Si no hay evaluaciones, simplemente no las mostramos
        setEvaluaciones({});
      }
    } catch (err) {
      console.error('Error loading cotizaciones:', err);
      setError('Error al cargar las cotizaciones de la convocatoria');
      setCotizacionesOriginales([]);
      setCotizaciones([]);
      setEvaluaciones({});
    } finally {
      setLoading(false);
    }
  };

  const convocatoriaSeleccionada = useMemo(() => {
    if (!selectedConvocatoria) return null;
    return convocatoriasOriginales.find((c) => c.nro_convocatoria === selectedConvocatoria) || null;
  }, [selectedConvocatoria, convocatoriasOriginales]);

  const ordenNotificadaParaConvocatoria = !!convocatoriaSeleccionada?.orden_notificada;

  useEffect(() => {
    if (ordenNotificadaParaConvocatoria) {
      setEditingCotizacion(null);
      setEvaluacionData({});
      setFichaCalificacionFile(null);
    }
  }, [ordenNotificadaParaConvocatoria, selectedConvocatoria]);

  const handleConvocatoriaChange = (e) => {
    const nroConvocatoria = e.target.value ? parseInt(e.target.value) : null;
    setSelectedConvocatoria(nroConvocatoria);
    setEditingCotizacion(null);
    setEvaluacionData({});
    setSearchSelectConvocatoria('');
    setShowConvocatoriaDropdown(false);
  };

  const handleSelectConvocatoriaSearch = (e) => {
    const value = e.target.value;
    setSearchSelectConvocatoria(value);
    setShowConvocatoriaDropdown(true);
    // Si el usuario está escribiendo algo diferente al texto de la convocatoria seleccionada, limpiar la selección
    if (selectedConvocatoria) {
      const conv = convocatoriasOriginales.find(c => c.nro_convocatoria === selectedConvocatoria);
      const expectedText = conv ? `${conv.COD_CONVOCATORIA} - ${conv.concepto || 'Sin concepto'}` : '';
      if (value !== expectedText) {
        setSelectedConvocatoria(null);
      }
    }
  };

  const handleSelectConvocatoria = (conv) => {
    setSelectedConvocatoria(conv.nro_convocatoria);
    setSearchSelectConvocatoria(`${conv.COD_CONVOCATORIA} - ${conv.concepto || 'Sin concepto'}`);
    setShowConvocatoriaDropdown(false);
    setEditingCotizacion(null);
    setEvaluacionData({});
  };

  // Filtrar convocatorias para el selector de búsqueda
  const getFilteredConvocatoriasForSelect = () => {
    if (!searchSelectConvocatoria) {
      return convocatoriasOriginales.slice(0, 10); // Mostrar solo las primeras 10 si no hay búsqueda
    }
    return convocatoriasOriginales.filter(conv => 
      (conv.COD_CONVOCATORIA && conv.COD_CONVOCATORIA.toString().toLowerCase().includes(searchSelectConvocatoria.toLowerCase())) ||
      (conv.concepto && conv.concepto.toLowerCase().includes(searchSelectConvocatoria.toLowerCase())) ||
      (conv.rubro && conv.rubro.toLowerCase().includes(searchSelectConvocatoria.toLowerCase())) ||
      (conv.nro_convocatoria && conv.nro_convocatoria.toString().includes(searchSelectConvocatoria))
    ).slice(0, 10); // Limitar a 10 resultados
  };

  const handleEdit = (cotizacion) => {
    if (ordenNotificadaParaConvocatoria) return;
    setEditingCotizacion(cotizacion.id_cotizacion);
    // Si existe una evaluación, usar esos datos, sino usar datos de la cotización
    const evaluacion = evaluaciones[cotizacion.id_cotizacion];
    setEvaluacionData({
      marca: evaluacion?.marca || cotizacion.marca || '',
      precio_ofertado: evaluacion?.precio_ofertado || cotizacion.precio_ofertado || '',
      plazo_entrega: evaluacion?.plazo_entrega || cotizacion.plazo_entrega || '',
      validez_cotizacion: evaluacion?.validez_cotizacion || cotizacion.validez_cotizacion || '',
      observaciones: evaluacion?.observaciones || '',
      seleccionado: evaluacion?.seleccionado || false
    });
  };

  const handleCancelEdit = () => {
    setEditingCotizacion(null);
    setEvaluacionData({});
    setFichaCalificacionFile(null);
  };

  const handleSave = async (cotizacionId) => {
    try {
      if (ordenNotificadaParaConvocatoria) {
        return;
      }
      setLoading(true);
      setError(null);

      // Validar que precio_ofertado sea un número válido si se proporciona
      if (evaluacionData.precio_ofertado) {
        const precio = parseFloat(evaluacionData.precio_ofertado);
        if (isNaN(precio)) {
          setNotification({
            show: true,
            title: 'Dato inválido',
            message: 'El precio ofertado debe ser un número válido',
            type: 'warning',
            onConfirm: null
          });
          setLoading(false);
          return;
        }
      }

      // Crear FormData para enviar datos y archivo
      const formData = new FormData();
      
      if (evaluacionData.marca) formData.append('marca', evaluacionData.marca);
      if (evaluacionData.precio_ofertado) {
        formData.append('precio_ofertado', parseFloat(evaluacionData.precio_ofertado));
      }
      if (evaluacionData.plazo_entrega) {
        formData.append('plazo_entrega', parseInt(evaluacionData.plazo_entrega));
      }
      if (evaluacionData.validez_cotizacion) {
        formData.append('validez_cotizacion', parseInt(evaluacionData.validez_cotizacion));
      }
      if (evaluacionData.observaciones) {
        formData.append('observaciones', evaluacionData.observaciones);
      }
      // Siempre enviar seleccionado (true o false)
      formData.append('seleccionado', evaluacionData.seleccionado ? 'true' : 'false');
      if (fichaCalificacionFile) {
        formData.append('ficha_calificacion_pdf', fichaCalificacionFile);
      }

      // Usar el nuevo endpoint de evaluaciones con FormData
      await api.patch(`/api/v1/evaluaciones/cotizacion/${cotizacionId}`, formData);
      
      // Recargar cotizaciones y evaluaciones
      if (selectedConvocatoria) {
        await loadCotizacionesByConvocatoria(selectedConvocatoria);
      }
      
      setEditingCotizacion(null);
      setEvaluacionData({});
      setFichaCalificacionFile(null);
      setNotification({
        show: true,
        title: 'Éxito',
        message: 'Criterios de evaluación guardados exitosamente',
        type: 'success',
        onConfirm: null
      });
    } catch (err) {
      console.error('Error saving evaluacion:', err);
      const errorMsg = formatApiErrorDetail(err, 'Error al guardar los criterios de evaluación');
      setError(errorMsg);
      setNotification({
        show: true,
        title: 'Error',
        message: errorMsg,
        type: 'error',
        onConfirm: null
      });
    } finally {
      setLoading(false);
    }
  };

  // Actualizar convocatorias cuando se cargan las originales
  useEffect(() => {
    setConvocatorias(convocatoriasOriginales);
  }, [convocatoriasOriginales]);

  // Filtrar cotizaciones usando useMemo
  const cotizacionesFiltradas = useMemo(() => {
    let filtradas = cotizacionesOriginales;

    // Filtro por búsqueda de proveedor
    if (searchProveedor) {
      filtradas = filtradas.filter(cot => 
        (cot.proveedor_razon_social && cot.proveedor_razon_social.toLowerCase().includes(searchProveedor.toLowerCase())) ||
        (cot.proveedor_ruc && cot.proveedor_ruc.includes(searchProveedor)) ||
        (cot.cod_cotizacion && cot.cod_cotizacion.toLowerCase().includes(searchProveedor.toLowerCase()))
      );
    }

    // Filtro por estado de evaluación
    if (filterEvaluadas === 'EVALUADAS') {
      filtradas = filtradas.filter(cot => evaluaciones[cot.id_cotizacion]);
    } else if (filterEvaluadas === 'PENDIENTES') {
      filtradas = filtradas.filter(cot => !evaluaciones[cot.id_cotizacion]);
    }

    return filtradas;
  }, [searchProveedor, filterEvaluadas, cotizacionesOriginales, evaluaciones]);

  // Aplicar filtros a cotizaciones
  useEffect(() => {
    setCotizaciones(cotizacionesFiltradas);
  }, [cotizacionesFiltradas]);

  // Estadísticas
  const getEstadisticas = () => {
    const total = cotizacionesOriginales.length;
    const evaluadas = Object.keys(evaluaciones).length;
    const pendientes = total - evaluadas;
    return { total, evaluadas, pendientes };
  };

  const estadisticas = getEstadisticas();

  const convocatoriaInfo = convocatoriaSeleccionada;

  const getEstadoBadgeVariant = (estado) => {
    switch(estado) {
      case 'ABIERTA':
        return 'success';
      case 'CERRADA':
        return 'secondary';
      case 'EVALUACION':
        return 'warning';
      case 'FINALIZADA':
        return 'info';
      default:
        return 'secondary';
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1 fw-bold">Evaluación de Cotizaciones</h1>
          <p className="text-muted mb-0">Ingrese los criterios de calificación de las cotizaciones</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Selector de Convocatoria con Búsqueda Mejorada */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Seleccionar Convocatoria</h5>
          <span className="badge bg-primary">{convocatoriasOriginales.length} convocatoria(s) disponible(s)</span>
        </div>
        
        <div className="row g-3">
          {/* Selector de convocatoria con búsqueda */}
          <div className="col-md-12">
            <label className="form-label">Convocatoria *</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar y seleccionar convocatoria..."
                value={searchSelectConvocatoria}
                onChange={handleSelectConvocatoriaSearch}
                onFocus={() => {
                  setShowConvocatoriaDropdown(true);
                  // Si hay una convocatoria seleccionada, mostrar su texto al hacer focus
                  if (convocatoriaInfo && !searchSelectConvocatoria) {
                    setSearchSelectConvocatoria(`${convocatoriaInfo.COD_CONVOCATORIA} - ${convocatoriaInfo.concepto || 'Sin concepto'}`);
                  }
                }}
                onBlur={() => {
                  // Delay para permitir el click en las opciones
                  setTimeout(() => {
                    setShowConvocatoriaDropdown(false);
                    // Si hay una convocatoria seleccionada, restaurar su texto
                    if (convocatoriaInfo && !searchSelectConvocatoria) {
                      setSearchSelectConvocatoria(`${convocatoriaInfo.COD_CONVOCATORIA} - ${convocatoriaInfo.concepto || 'Sin concepto'}`);
                    }
                  }, 200);
                }}
                disabled={loading}
              />
              {showConvocatoriaDropdown && (
                <div 
                  className="border rounded shadow-lg bg-white"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    marginTop: '2px'
                  }}
                >
                  {getFilteredConvocatoriasForSelect().length > 0 ? (
                    getFilteredConvocatoriasForSelect().map((conv) => (
                      <div
                        key={conv.nro_convocatoria}
                        className="p-2 border-bottom cursor-pointer"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedConvocatoria === conv.nro_convocatoria ? '#e7f3ff' : 'white'
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectConvocatoria(conv);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f0f0f0';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = selectedConvocatoria === conv.nro_convocatoria ? '#e7f3ff' : 'white';
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong>{conv.COD_CONVOCATORIA}</strong>
                            <div className="small text-muted">
                              {conv.concepto || 'Sin concepto'}
                            </div>
                            <div className="small">
                              <span className="text-muted">Rubro:</span> {conv.rubro || 'N/A'}
                            </div>
                          </div>
                          <span className={`badge bg-${getEstadoBadgeVariant(conv.estado)} ms-2`}>
                            {conv.estado}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-center text-muted">
                      <i className="bi bi-search me-2"></i>
                      No se encontraron convocatorias
                    </div>
                  )}
                </div>
              )}
            </div>
            {selectedConvocatoria && (
              <small className="text-muted d-block mt-1">
                <i className="bi bi-check-circle text-success me-1"></i>
                Convocatoria seleccionada
              </small>
            )}
          </div>
        </div>

        {/* Información de la convocatoria seleccionada */}
        {convocatoriaInfo && (
          <div className="mt-3 p-3 bg-light rounded">
            <div className="row">
              <div className="col-md-3">
                <strong>Código:</strong> {convocatoriaInfo.COD_CONVOCATORIA || 'N/A'}
              </div>
              <div className="col-md-3">
                <strong>Rubro:</strong> {convocatoriaInfo.rubro || 'N/A'}
              </div>
              <div className="col-md-3">
                <strong>Concepto:</strong> {convocatoriaInfo.concepto || 'N/A'}
              </div>
              <div className="col-md-3">
                <strong>Estado:</strong>{' '}
                <span className={`badge bg-${getEstadoBadgeVariant(convocatoriaInfo.estado)}`}>
                  {convocatoriaInfo.estado || 'N/A'}
                </span>
              </div>
            </div>
            <div className="row mt-2">
              <div className="col-md-3">
                <strong>Nro Pedido:</strong> {convocatoriaInfo.nro_pedido || 'N/A'}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Tabla de Cotizaciones */}
      {selectedConvocatoria && (
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          {ordenNotificadaParaConvocatoria && (
            <div className="alert alert-secondary mb-3" role="alert">
              <i className="bi bi-lock me-2"></i>
              Esta convocatoria ya tiene una orden notificada al proveedor. La evaluación queda en solo lectura;
              no puede usar &quot;Evaluar&quot;.
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">
                Proveedores que Postularon
              </h5>
              <div className="d-flex gap-3 align-items-center">
                <span className="badge bg-primary">{cotizaciones.length} de {estadisticas.total} mostrados</span>
                <span className="badge bg-success">{estadisticas.evaluadas} evaluadas</span>
                <span className="badge bg-warning text-dark">{estadisticas.pendientes} pendientes</span>
              </div>
            </div>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => loadCotizacionesByConvocatoria(selectedConvocatoria)}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Actualizar
            </button>
          </div>

          {/* Filtros de búsqueda para cotizaciones */}
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <label className="form-label small">
                <i className="bi bi-search me-1"></i>
                Buscar Proveedor
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Razón social, RUC, código de cotización..."
                value={searchProveedor}
                onChange={(e) => setSearchProveedor(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small">
                <i className="bi bi-funnel me-1"></i>
                Estado de Evaluación
              </label>
              <select
                className="form-select form-select-sm"
                value={filterEvaluadas}
                onChange={(e) => setFilterEvaluadas(e.target.value)}
              >
                <option value="TODAS">Todas</option>
                <option value="EVALUADAS">Evaluadas</option>
                <option value="PENDIENTES">Pendientes</option>
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              {(searchProveedor || filterEvaluadas !== 'TODAS') && (
                <button
                  className="btn btn-sm btn-outline-secondary w-100"
                  onClick={() => {
                    setSearchProveedor('');
                    setFilterEvaluadas('TODAS');
                  }}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Limpiar
                </button>
              )}
            </div>
          </div>

          {loading && cotizaciones.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="mt-3 text-muted">Cargando cotizaciones...</p>
              </div>
            </div>
          ) : cotizaciones.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              No hay proveedores que hayan postulado para esta convocatoria.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#667eea', color: 'white' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Proveedor</th>
                    <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>RUC</th>
                    <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Marca</th>
                    <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Precio Ofertado</th>
                    <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Plazo Entrega (días)</th>
                    <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Validez Cotización (días)</th>
                    <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Ficha Calificación</th>
                    <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Observaciones</th>
                    <th style={{ padding: '1rem', textAlign: 'center', border: '1px solid #ddd' }}>Ganador</th>
                    <th style={{ padding: '1rem', textAlign: 'center', border: '1px solid #ddd' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cotizaciones.map((cot) => {
                    const evaluacion = evaluaciones[cot.id_cotizacion];
                    const estaEvaluada = !!evaluacion;
                    const esGanador = evaluacion?.seleccionado || false;
                    return (
                      <tr 
                        key={cot.id_cotizacion}
                        style={{ 
                          backgroundColor: esGanador ? '#fff3cd' : (estaEvaluada ? '#f0f8f0' : 'white'),
                          borderLeft: esGanador ? '4px solid #ffc107' : (estaEvaluada ? '4px solid #28a745' : '4px solid transparent')
                        }}
                      >
                        <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                          <div className="d-flex align-items-center gap-2">
                            {esGanador && (
                              <span className="badge bg-warning text-dark" title="Ganador">
                                <i className="bi bi-trophy-fill"></i>
                              </span>
                            )}
                            {estaEvaluada && !esGanador && (
                              <span className="badge bg-success" title="Evaluada">
                                <i className="bi bi-check-circle"></i>
                              </span>
                            )}
                            {!estaEvaluada && (
                              <span className="badge bg-secondary" title="Pendiente de evaluación">
                                <i className="bi bi-clock"></i>
                              </span>
                            )}
                            <span>{cot.proveedor_razon_social || 'N/A'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                          {cot.proveedor_ruc || 'N/A'}
                        </td>
                      {editingCotizacion === cot.id_cotizacion ? (
                        <>
                          <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={evaluacionData.marca || ''}
                              onChange={(e) => setEvaluacionData({ ...evaluacionData, marca: e.target.value })}
                              placeholder="Marca"
                            />
                          </td>
                          <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control form-control-sm"
                              value={evaluacionData.precio_ofertado || ''}
                              onChange={(e) => setEvaluacionData({ ...evaluacionData, precio_ofertado: e.target.value })}
                              placeholder="0.00"
                            />
                          </td>
                          <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={evaluacionData.plazo_entrega || ''}
                              onChange={(e) => setEvaluacionData({ ...evaluacionData, plazo_entrega: e.target.value })}
                              placeholder="Días"
                            />
                          </td>
                          <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={evaluacionData.validez_cotizacion || ''}
                              onChange={(e) => setEvaluacionData({ ...evaluacionData, validez_cotizacion: e.target.value })}
                              placeholder="Días"
                            />
                          </td>
                          <td colSpan="2" style={{ padding: '1rem', border: '1px solid #ddd' }}>
                            <div className="d-flex flex-column gap-2">
                              <div>
                                <label className="form-label small">
                                  Ficha de Calificación (PDF) <span className="text-muted">(Solo 1 archivo por evaluación)</span>
                                </label>
                                <input
                                  type="file"
                                  accept=".pdf"
                                  className="form-control form-control-sm"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      // Validar que sea PDF
                                      if (file.type !== 'application/pdf') {
                                        setNotification({
                                          show: true,
                                          title: 'Formato incorrecto',
                                          message: 'Solo se permiten archivos PDF',
                                          type: 'warning',
                                          onConfirm: null
                                        });
                                        e.target.value = '';
                                        return;
                                      }
                                      // Validar tamaño (máximo 50MB)
                                      if (file.size > 50 * 1024 * 1024) {
                                        setNotification({
                                          show: true,
                                          title: 'Archivo muy grande',
                                          message: 'El archivo no debe exceder 50MB',
                                          type: 'warning',
                                          onConfirm: null
                                        });
                                        e.target.value = '';
                                        return;
                                      }
                                      setFichaCalificacionFile(file);
                                    } else {
                                      setFichaCalificacionFile(null);
                                    }
                                  }}
                                  title="Subir ficha de calificación (PDF) - Solo 1 archivo por evaluación"
                                />
                                {(() => {
                                  const evaluacion = evaluaciones[cot.id_cotizacion];
                                  if (evaluacion?.ficha_calificacion_pdf) {
                                    const sanitizeFragment = (value) => {
                                      if (!value) return '';
                                      return String(value).trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
                                    };
                                    const codCotizacion = cot.cod_cotizacion || cot.id_cotizacion;
                                    const folder = sanitizeFragment(codCotizacion);
                                    const fileUrl = `${getPublicUploadsOrigin()}/uploads/evaluaciones/${folder}/${evaluacion.ficha_calificacion_pdf}`;
                                    return (
                                      <small className="text-muted d-block mt-1">
                                        <i className="bi bi-file-pdf me-1"></i>
                                        Archivo actual: <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-primary">Ver PDF</a>
                                        {fichaCalificacionFile && (
                                          <span className="text-warning ms-2">
                                            <i className="bi bi-exclamation-triangle me-1"></i>
                                            Se reemplazará con el nuevo archivo
                                          </span>
                                        )}
                                      </small>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              <div>
                                <label className="form-label small">Observaciones</label>
                                <textarea
                                  className="form-control form-control-sm"
                                  rows="2"
                                  value={evaluacionData.observaciones || ''}
                                  onChange={(e) => setEvaluacionData({ ...evaluacionData, observaciones: e.target.value })}
                                  placeholder="Observaciones adicionales..."
                                />
                              </div>
                              <div>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`seleccionado-${cot.id_cotizacion}`}
                                    checked={evaluacionData.seleccionado || false}
                                    onChange={(e) => setEvaluacionData({ ...evaluacionData, seleccionado: e.target.checked })}
                                  />
                                  <label className="form-check-label small" htmlFor={`seleccionado-${cot.id_cotizacion}`}>
                                    <i className="bi bi-trophy-fill text-warning me-1"></i>
                                    Marcar como Ganador
                                  </label>
                                </div>
                                <small className="text-muted d-block mt-1">
                                  Al marcar como ganador, se desmarcarán automáticamente otros ganadores de esta convocatoria.
                                </small>
                              </div>
                              <div className="d-flex gap-2 justify-content-center">
                                <button
                                  onClick={() => handleSave(cot.id_cotizacion)}
                                  className="btn btn-success btn-sm"
                                  disabled={loading || ordenNotificadaParaConvocatoria}
                                  title="Guardar"
                                >
                                  <i className="bi bi-check me-1"></i>
                                  Guardar
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="btn btn-secondary btn-sm"
                                  disabled={loading}
                                  title="Cancelar"
                                >
                                  <i className="bi bi-x me-1"></i>
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                            {(() => {
                              const evaluacion = evaluaciones[cot.id_cotizacion];
                              return evaluacion?.marca || cot.marca || <span className="text-muted">No especificado</span>;
                            })()}
                          </td>
                          <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                            {(() => {
                              const evaluacion = evaluaciones[cot.id_cotizacion];
                              const precio = evaluacion?.precio_ofertado || cot.precio_ofertado;
                              return precio 
                                ? `S/ ${parseFloat(precio).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : <span className="text-muted">No especificado</span>;
                            })()}
                          </td>
                          <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                            {(() => {
                              const evaluacion = evaluaciones[cot.id_cotizacion];
                              const plazo = evaluacion?.plazo_entrega || cot.plazo_entrega;
                              return plazo 
                                ? `${plazo} días`
                                : <span className="text-muted">No especificado</span>;
                            })()}
                          </td>
                          <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                            {(() => {
                              const evaluacion = evaluaciones[cot.id_cotizacion];
                              const validez = evaluacion?.validez_cotizacion || cot.validez_cotizacion;
                              return validez 
                                ? `${validez} días`
                                : <span className="text-muted">No especificado</span>;
                            })()}
                          </td>
                          <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                            {(() => {
                              const evaluacion = evaluaciones[cot.id_cotizacion];
                              if (evaluacion?.ficha_calificacion_pdf) {
                                const sanitizeFragment = (value) => {
                                  if (!value) return '';
                                  return String(value).trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
                                };
                                const codCotizacion = cot.cod_cotizacion || cot.id_cotizacion;
                                const folder = sanitizeFragment(codCotizacion);
                                const fileUrl = `${getPublicUploadsOrigin()}/uploads/evaluaciones/${folder}/${evaluacion.ficha_calificacion_pdf}`;
                                return (
                                  <a 
                                    href={fileUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-info btn-sm"
                                    title="Ver ficha de calificación"
                                  >
                                    <i className="bi bi-file-pdf me-1"></i>
                                    Ver PDF
                                  </a>
                                );
                              }
                              return <span className="text-muted">No disponible</span>;
                            })()}
                          </td>
                          <td style={{ padding: '1rem', border: '1px solid #ddd', maxWidth: '200px' }}>
                            {(() => {
                              const evaluacion = evaluaciones[cot.id_cotizacion];
                              if (evaluacion?.observaciones) {
                                return (
                                  <small className="text-muted" title={evaluacion.observaciones}>
                                    {evaluacion.observaciones.length > 50 
                                      ? evaluacion.observaciones.substring(0, 50) + '...'
                                      : evaluacion.observaciones}
                                  </small>
                                );
                              }
                              return <span className="text-muted">Sin observaciones</span>;
                            })()}
                          </td>
                          <td style={{ padding: '1rem', border: '1px solid #ddd', textAlign: 'center' }}>
                            {(() => {
                              const evaluacion = evaluaciones[cot.id_cotizacion];
                              if (evaluacion?.seleccionado) {
                                return (
                                  <span className="badge bg-warning text-dark" style={{ fontSize: '0.9rem' }}>
                                    <i className="bi bi-trophy-fill me-1"></i>
                                    Ganador
                                  </span>
                                );
                              }
                              return <span className="text-muted">-</span>;
                            })()}
                          </td>
                          <td style={{ padding: '1rem', border: '1px solid #ddd', textAlign: 'center' }}>
                            <button
                              onClick={() => handleEdit(cot)}
                              className="btn btn-primary btn-sm"
                              disabled={loading || ordenNotificadaParaConvocatoria}
                              title={
                                ordenNotificadaParaConvocatoria
                                  ? 'No disponible: ya se notificó la orden al proveedor'
                                  : 'Editar criterios de evaluación'
                              }
                            >
                              <i className="bi bi-pencil me-1"></i>
                              Evaluar
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!selectedConvocatoria && (
        <div style={{
          background: 'white',
          padding: '3rem',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          color: '#666'
        }}>
          <i className="bi bi-inbox" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}></i>
          <p>Seleccione una convocatoria para ver las cotizaciones y comenzar la evaluación</p>
        </div>
      )}

      {/* Modal de Notificaciones */}
      <NotificationModal
        show={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        onConfirm={notification.onConfirm}
        showCancel={notification.type === 'confirm'}
      />
    </div>
  );
};

export default Evaluacion;

